/**
 * Evidence before a skill is approved (docs/plans/2026-10-odysseus-adoption.md, item 2): the result
 * in words beside Approve, and the Evaluate dialog that asks one node to run the skill's tasks with
 * and without it. Words come from skillEvidenceWords.ts.
 */

import type { CatalogueEvaluation, CatalogueEvaluationOrder } from '@aioe/shared';
import { Modal } from '@heroui/react';
import { useState } from 'react';

import { problemText, routes } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { Button, ChoiceSelect, ErrorNote, inputClass, StatusMark } from './ui.tsx';
import { evidenceMeta, evidenceSentence, whenWords } from './skillEvidenceWords.ts';

/** The evidence, then an outstanding or failed evaluation. Nothing when there is neither. */
export function SkillEvidence({ evaluation, evaluating }: { evaluation?: CatalogueEvaluation; evaluating?: CatalogueEvaluationOrder }) {
  if (!evaluation && !evaluating) return null;
  const pending = evaluating?.state === 'pending';
  const didNotRun = evaluating && (evaluating.state === 'failed' || evaluating.state === 'refused' || evaluating.state === 'expired');
  return (
    <div className="grid gap-1 text-sm">
      {evaluation && (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <StatusMark state={evaluation.result.verdict === 'pass' ? 'connected' : 'error'} label={evaluation.result.verdict === 'pass' ? 'Passed' : 'Did not pass'} />
            {evaluation.stale && <StatusMark state="disabled" label="Stale" />}
          </div>
          <div>{evidenceSentence(evaluation)}</div>
          <div className="text-xs text-muted">
            {evidenceMeta(evaluation)}
            {evaluation.stale ? ' The skill or the organisation’s default model has changed since.' : ''}
          </div>
        </>
      )}
      {pending && (
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted">
          <StatusMark state="working" label="Pending" />
          <span>
            {evaluation ? 'A new evaluation was asked of' : 'Asked of'} {evaluating.deviceName} {whenWords(evaluating.issuedAt)}; it answers when it next reports in.
          </span>
        </div>
      )}
      {didNotRun && (
        <div className="text-xs text-muted">
          The last evaluation on {evaluating.deviceName} did not run{evaluating.message ? `: ${evaluating.message.replace(/\.$/, '')}` : ''}.
        </div>
      )}
    </div>
  );
}

/** Ask one online node to evaluate a skill: a published entry or a pending submission. */
/** A node has a project open when a workspace it reports belongs to a project, or a remote-controlled session runs in a repository. */
function hasProjectOpen(n: { workspaces: { projectId?: string }[]; remoteSessions?: { rootId?: string }[] }): boolean {
  return n.workspaces.some((w) => !!w.projectId) || (n.remoteSessions ?? []).some((s) => !!s.rootId);
}

/** The approved nodes online now, the ones with a project open first, as a dialog's choices (the Evaluate and Verify dialogs). */
export function useOnlineNodes() {
  const nodes = useLoad(() => routes.nodes(), []);
  const online = (nodes.data ?? []).filter((n) => n.kind === 'node' && n.online && (n.approval.state === 'approved' || n.approval.state === 'pending-surface')).sort((a, b) => Number(hasProjectOpen(b)) - Number(hasProjectOpen(a)) || a.hostname.localeCompare(b.hostname));
  const options = Object.fromEntries(online.map((n) => [n.deviceId, hasProjectOpen(n) ? `${n.hostname} (project open)` : n.hostname])) as Record<string, string>;
  return { loaded: !!nodes.data, online, options };
}

export function EvaluateDialog({ target, onClose, onIssued }: { target: { kind: 'entry' | 'submission'; id: string; name: string }; onClose: () => void; onIssued: () => void }) {
  const { loaded, online, options } = useOnlineNodes();
  const [deviceId, setDeviceId] = useState<string>();
  const [tasks, setTasks] = useState('');
  const [worker, setWorker] = useState('');
  const [judge, setJudge] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>();
  const chosen = deviceId && options[deviceId] ? deviceId : online[0]?.deviceId;
  const chosenNode = online.find((n) => n.deviceId === chosen);
  const lines = tasks.split('\n').map((t) => t.trim()).filter(Boolean);

  async function send() {
    if (!chosen) return;
    setSending(true);
    setError(undefined);
    const body = { deviceId: chosen, ...(lines.length ? { tasks: lines.slice(0, 5) } : {}), ...(worker.trim() ? { worker: worker.trim() } : {}), ...(judge.trim() ? { judge: judge.trim() } : {}) };
    try {
      await (target.kind === 'entry' ? routes.evaluateEntry(target.id, body) : routes.evaluateSubmission(target.id, body));
      onIssued();
      onClose();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setSending(false);
    }
  }

  return (
    <Modal isOpen onOpenChange={(open) => !open && onClose()}>
      <Modal.Backdrop>
        <Modal.Container placement="center" size="md">
          <Modal.Dialog aria-label={`Evaluate ${target.name}`}>
            <Modal.Header>
              <Modal.Heading>Evaluate {target.name}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="grid gap-3 p-1">
              <p className="m-0 text-sm text-muted">The node runs each task twice, once with the skill and once without it, and a judge model grades both. Nothing is published on the result.</p>
              {loaded && !online.length ? (
                <p className="m-0 text-sm">No node is online. A skill is evaluated on a node, unattended: bring one online and try again.</p>
              ) : (
                <ChoiceSelect label="Node" value={chosen} options={options} onChange={setDeviceId} placeholder={loaded ? 'Choose a node' : 'Loading nodes…'} isDisabled={!online.length} />
              )}
              {chosenNode && !hasProjectOpen(chosenNode) && (
                <p className="m-0 text-xs text-muted">This node has no project open, so it will refuse the evaluation until one of its workbenches runs.</p>
              )}
              <label className="grid gap-1 text-xs text-muted">
                Tasks, one a line (up to five; left empty, the skill’s tests.md, else the judge writes them)
                <textarea className={`${inputClass} min-h-20`} value={tasks} onChange={(e) => setTasks(e.target.value)} placeholder={'Write release notes for version 2.4\nWrite release notes for a hotfix'} />
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="grid gap-1 text-xs text-muted">
                  Worker model (optional)
                  <input className={`${inputClass} mono`} value={worker} onChange={(e) => setWorker(e.target.value)} placeholder="The node’s own" />
                </label>
                <label className="grid gap-1 text-xs text-muted">
                  Judge model (optional)
                  <input className={`${inputClass} mono`} value={judge} onChange={(e) => setJudge(e.target.value)} placeholder="The node’s own" />
                </label>
              </div>
              {lines.length > 5 && <p className="m-0 text-xs text-muted">Only the first five tasks are sent.</p>}
              <ErrorNote error={error} />
            </Modal.Body>
            <Modal.Footer>
              <Button size="sm" variant="tertiary" onPress={onClose}>
                Cancel
              </Button>
              <Button size="sm" isPending={sending} isDisabled={!chosen} onPress={() => void send()}>
                Evaluate
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
